import { Box, Button, Select } from '@nerdwallet/currency';
import Accordion from '@nerdwallet/react-accordion';
import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import { Fragment, useId } from 'react';
import SegmentImpression from '~/app/components/segment-impression';
import { useMediaQuery } from '~/app/lib/use-media-query';
import {
  SegmentTrackingForm,
  SegmentTrackingProps,
} from '../../segment/segment-tracking-form';
import { useDynamicAnalytics } from '../../segment/use-dynamic-analytics';
import {
  DynamicBulletList,
  DynamicBulletListForm,
  DynamicBulletListProps,
} from '../dynamic-bullet-list/dynamic-bullet-list-component';
import {
  DynamicTextComponent,
  DynamicTextComponentProps,
  DynamicTextForm,
} from '../dynamic-text/dynamic-text-component';
import { DynamicAccordionSpacingsForm } from './dynamic-accordion-spacings-form';
import { SpacingsType, useDynamicAccordion } from './use-dynamic-accordion';

import styles from './dynamic-accordion.module.scss';

const CONTENT_TYPE_OPTIONS = [
  { label: 'Text', value: 'text' },
  { label: 'Bullet List', value: 'bulletList' },
];

type AccordionSections = Array<{
  bodyContentType?: 'text' | 'bulletList';
  body?: DynamicTextComponentProps & { spacings?: SpacingsType };
  bodyBulletList?: DynamicBulletListProps;
  header?: DynamicTextComponentProps & { spacings?: SpacingsType };
  footer?: DynamicTextComponentProps & { spacings?: SpacingsType };
}>;

const DEFAULT_ACCORDION_SECTIONS: AccordionSections = [
  {
    bodyContentType: 'text',
    header: { text: 'This is section 1' },
    body: { text: 'This is section 1 body', fontSize: 14 },
    footer: { text: 'This is section 1' },
  },
  {
    bodyContentType: 'text',
    header: { text: 'This is section 2' },
    body: { text: 'This is section 2 body', fontSize: 14 },
    footer: { text: 'This is section 2' },
  },
];

export const DynamicAccordion = ({
  sections = DEFAULT_ACCORDION_SECTIONS,
  segment,
}: {
  sections?: AccordionSections;
  segment?: SegmentTrackingProps;
}) => {
  const nodeId = useId();
  const screenSize = useMediaQuery();
  const isMobile = screenSize === 'sm';
  const isTablet = screenSize === 'md';

  const { impressionViewProps, handleTrackInteraction } = useDynamicAnalytics({
    enableInteractionTracking: segment?.enableInteractionTracking || false,
    entityName: segment?.entityName || '',
    impressionSection: segment?.impressionSection || '',
    impressionPosition: segment?.impressionPosition || '',
    extraProps: {
      sub_location: 'accordion',
    },
  });

  const items = sections?.map((section, index) => {
    let containerOptions = {
      header: { ...section.header?.spacings },
      body: { ...section.body?.spacings },
      footer: { ...section.footer?.spacings },
    };
    if (isTablet) {
      containerOptions = {
        header: { ...section.header?.spacings?.tablet },
        body: { ...section.body?.spacings?.tablet },
        footer: { ...section.footer?.spacings?.tablet },
      };
    }
    if (isMobile) {
      containerOptions = {
        header: { ...section.header?.spacings?.mobile },
        body: { ...section.body?.spacings?.mobile },
        footer: { ...section.footer?.spacings?.mobile },
      };
    }

    return {
      id: `section-${index}-${nodeId}`,
      key: `section-${index}-${nodeId}`,
      title: (
        <Box
          style={{ ...containerOptions.header }}
          onClick={handleTrackInteraction}
        >
          <DynamicTextComponent {...section.header} />
        </Box>
      ),
      children: (
        <div id={`section-${index}-${nodeId}-content`}>
          <Box style={{ ...containerOptions.body }}>
            {section.bodyContentType === 'text' && section.body && (
              <DynamicTextComponent {...section.body} />
            )}
            {section.bodyContentType === 'bulletList' &&
              section.bodyBulletList && (
                <DynamicBulletList {...section.bodyBulletList} />
              )}
          </Box>
          {section.footer && section.footer.text && (
            <div
              className={styles.sectionFooter}
              style={{ ...containerOptions.footer }}
            >
              <DynamicTextComponent {...section.footer} />
            </div>
          )}
        </div>
      ),
    };
  });

  return (
    <SegmentImpression
      eventName="Accordion Viewed"
      eventProps={impressionViewProps}
      className={styles.segmentImpressionWrapper}
    >
      <div style={{ width: 'inherit' }}>
        <Accordion items={items} id={nodeId} />
      </div>
    </SegmentImpression>
  );
};

export const DynamicAccordionForm = () => {
  const {
    handleBodyContentTypeChange,
    getSections,
    addSection,
    deleteSection,
  } = useDynamicAccordion();

  const sections: AccordionSections = getSections();

  return (
    <div className={styles.editorWrapper}>
      <SegmentTrackingForm />
      <div className={styles.accordionWrapper}>
        <Accordion
          items={sections.map((_section, index) => ({
            id: `section-${index}`,
            key: `section-${index}`,
            title: (
              <div className={styles.sectionContainer}>
                <span>{`Section #${index + 1}`}</span>
                <Trash
                  onClick={() => deleteSection(index)}
                  size={15}
                  title="delete this element"
                  className="trash"
                />
              </div>
            ),
            children: (
              <Accordion
                items={
                  [
                    {
                      id: `section-${index}-header`,
                      title: 'Header',
                      children: (
                        <Fragment>
                          <DynamicTextForm
                            fieldPath={`sections[${index}].header`}
                          />

                          <Box
                            paddingTop={2}
                            id="spacings-header"
                            className={styles.accordionWrapper}
                          >
                            <Accordion
                              items={[
                                {
                                  title: (
                                    <Box
                                      className={
                                        styles.accordionHeaderContainer
                                      }
                                    >
                                      <span>Spacing Options</span>
                                    </Box>
                                  ),
                                  children: (
                                    <DynamicAccordionSpacingsForm
                                      fieldPath={`sections[${index}].header`}
                                      spacings={_section?.header?.spacings}
                                    />
                                  ),
                                  reducedPadding: true,
                                },
                              ]}
                            />
                          </Box>
                        </Fragment>
                      ),
                    },
                    {
                      id: `section-${index}-body`,
                      title: 'Body',
                      children: (
                        <Fragment>
                          <Box paddingY={2}>
                            <Select
                              fullWidth
                              value={_section?.bodyContentType}
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) => {
                                handleBodyContentTypeChange(e, index);
                              }}
                              label="Content Type"
                              options={CONTENT_TYPE_OPTIONS}
                            />
                          </Box>

                          <Box
                            paddingTop={2}
                            id="spacings-body"
                            className={styles.accordionWrapper}
                          >
                            <Accordion
                              items={[
                                {
                                  title: (
                                    <Box
                                      className={
                                        styles.accordionHeaderContainer
                                      }
                                    >
                                      <span>Spacing Options</span>
                                    </Box>
                                  ),
                                  children: (
                                    <DynamicAccordionSpacingsForm
                                      fieldPath={`sections[${index}].body`}
                                      spacings={_section?.body?.spacings}
                                    />
                                  ),
                                  reducedPadding: true,
                                },
                              ]}
                            />
                          </Box>

                          {_section?.bodyContentType === 'text' && (
                            <DynamicTextForm
                              fieldPath={`sections[${index}].body`}
                            />
                          )}
                          {_section?.bodyContentType === 'bulletList' && (
                            <DynamicBulletListForm
                              fieldPath={`sections[${index}].bodyBulletList`}
                            />
                          )}
                        </Fragment>
                      ),
                    },
                    {
                      id: `section-${index}-footer`,
                      title: 'Footer',
                      children: (
                        <Fragment>
                          <DynamicTextForm
                            fieldPath={`sections[${index}].footer`}
                          />

                          <Box
                            paddingTop={2}
                            id="spacings-footer"
                            className={styles.accordionWrapper}
                          >
                            <Accordion
                              items={[
                                {
                                  title: (
                                    <Box
                                      className={
                                        styles.accordionHeaderContainer
                                      }
                                    >
                                      <span>Spacing Options</span>
                                    </Box>
                                  ),
                                  children: (
                                    <DynamicAccordionSpacingsForm
                                      fieldPath={`sections[${index}].footer`}
                                      spacings={_section?.footer?.spacings}
                                    />
                                  ),
                                  reducedPadding: true,
                                },
                              ]}
                            />
                          </Box>
                        </Fragment>
                      ),
                    },
                  ] as Array<{
                    id: string;
                    title: string;
                    children: React.ReactNode;
                  }>
                }
              />
            ),
          }))}
        />
      </div>
      <Button secondary block onClick={addSection}>
        Add Section
      </Button>
    </div>
  );
};
